import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Save, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Card, EmptyState, Field } from "@/components/ranger/PageHeader";
import { ConfirmButton } from "@/components/ranger/ConfirmButton";
import { BTN_PRIMARY, BTN_SECONDARY } from "@/pages/Servers";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";

const inputCls = "border-[#1E2D4A] bg-[#050811]";
const itemsToText = (items = []) => items.map((i) => `${i.shortname} ${i.amount}`).join("\n");
const textToItems = (t) => t.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const [s, a] = l.split(/\s+/); return { shortname: s.toLowerCase(), amount: Number(a || 1) }; });
const rolesToText = (r = []) => r.map((x) => `${x.role_id} ${x.label}`).join("\n");
const textToRoles = (t) => t.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const [id, ...rest] = l.split(/\s+/); return { role_id: id, label: rest.join(" ") || "Role" }; });
const kvToText = (o = {}) => Object.entries(o).map(([k, v]) => `${k}=${v}`).join("\n");
const textToKv = (t) => Object.fromEntries(t.split("\n").map((l) => l.trim()).filter(Boolean).map((l) => { const i = l.indexOf("="); return i < 0 ? [l, ""] : [l.slice(0, i).trim(), l.slice(i + 1).trim()]; }));
const TO = { items: itemsToText, roles: rolesToText, kv: kvToText, lines: (v) => (v || []).join("\n") };
const FROM = { items: textToItems, roles: textToRoles, kv: textToKv, lines: (t) => t.split("\n").map((s) => s.trim()).filter(Boolean) };

const FieldInput = ({ f, value, onChange }) => {
  const tid = `field-${f.key}`;
  if (f.type === "switch") return <Switch data-testid={tid} checked={!!value} onCheckedChange={onChange} />;
  if (f.type === "textarea") return <Textarea data-testid={tid} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className={inputCls} />;
  if (["items", "roles", "kv", "lines"].includes(f.type)) return <Textarea data-testid={tid} rows={4} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={f.placeholder || "wood 1000\nrifle.ak 1"} className={`${inputCls} font-mono`} />;
  if (f.type === "multi") return (
    <div data-testid={tid} className="flex flex-wrap gap-2">{f.options.map(([v, l]) => {
      const on = (value || []).includes(v);
      return <button type="button" key={v} data-testid={`${tid}-${v}`} onClick={() => onChange(on ? value.filter((x) => x !== v) : [...(value || []), v])} className={`rounded-sm border px-3 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${on ? "border-[#E5202E] bg-[#E5202E]/15 text-[#E5202E]" : "border-[#1E2D4A] text-slate-400 hover:text-slate-200"}`}>{l}</button>;
    })}</div>
  );
  if (f.type === "select") return (
    <Select value={String(value ?? f.options[0][0])} onValueChange={(v) => onChange(f.numeric ? Number(v) : v)}>
      <SelectTrigger data-testid={tid} className={inputCls}><SelectValue /></SelectTrigger>
      <SelectContent className="border-[#1E2D4A] bg-[#0B1120]">{f.options.map(([v, l]) => <SelectItem key={v} value={String(v)}>{l}</SelectItem>)}</SelectContent>
    </Select>
  );
  return <Input data-testid={tid} type={f.type || "text"} value={value ?? ""} onChange={(e) => onChange(f.type === "number" ? Number(e.target.value) : e.target.value)} className={`${inputCls} ${f.type === "number" ? "font-mono" : ""}`} />;
};

export const FormFields = ({ fields, values, setValues }) => (
  <div className="grid gap-4 sm:grid-cols-2">
    {fields.filter((f) => !f.showIf || f.showIf(values)).map((f) => (
      <div key={f.key} className={f.wide || ["textarea", "items", "multi", "roles", "kv"].includes(f.type) ? "sm:col-span-2" : ""}>
        {f.type === "switch" ? (
          <label className="flex items-center justify-between gap-4 rounded-sm border border-[#1E2D4A] px-3 py-2.5">
            <span><span className="block text-sm text-slate-200">{f.label}</span>{f.hint && <span className="block text-xs text-slate-500">{f.hint}</span>}</span>
            <FieldInput f={f} value={values[f.key]} onChange={(v) => setValues({ ...values, [f.key]: v })} />
          </label>
        ) : <Field label={f.label} hint={f.hint}><FieldInput f={f} value={values[f.key]} onChange={(v) => setValues({ ...values, [f.key]: v })} /></Field>}
      </div>
    ))}
  </div>
);

const toForm = (fields, row) => Object.fromEntries(fields.map((f) => [f.key, TO[f.type] ? TO[f.type](row?.[f.key]) : row?.[f.key] ?? f.default]));
const fromForm = (fields, v) => Object.fromEntries(fields.filter((f) => !f.showIf || f.showIf(v)).map((f) => [f.key, FROM[f.type] ? FROM[f.type](v[f.key] || "") : v[f.key]]));

export const ConfigForm = ({ cid, field, fields, title, icon, testId }) => {
  const { can } = useAuth();
  const [v, setV] = useState(null);
  useEffect(() => { api.get(`/communities/${cid}/${field}-config`).then((r) => setV(r.data)); }, [cid, field]);
  if (!v) return null;
  const save = async (e) => {
    e.preventDefault();
    try { const { data } = await api.put(`/communities/${cid}/${field}-config`, fromForm(fields, v)); setV(data); toast.success("Saved"); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Card title={title} icon={icon} testId={testId || `${field}-config-card`} className="mb-5">
      <form onSubmit={save} className="space-y-4">
        <FormFields fields={fields} values={v} setValues={setV} />
        {can("admin") && <button data-testid={`${testId || field}-config-save`} className={BTN_PRIMARY}><Save className="h-4 w-4" />Save</button>}
      </form>
    </Card>
  );
};

export const CrudTable = ({ cid, path, title, icon, fields, columns, emptyText, rowActions }) => {
  const { can } = useAuth();
  const [rows, setRows] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api.get(`/communities/${cid}/${path}`).then((r) => setRows(r.data)), [cid, path]);
  useEffect(() => { load(); }, [load]);
  const save = async (e) => {
    e.preventDefault();
    const body = fromForm(fields, edit.values);
    try {
      if (edit.id) await api.put(`/communities/${cid}/${path}/${edit.id}`, body); else await api.post(`/communities/${cid}/${path}`, body);
      toast.success("Saved"); setEdit(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (id) => { try { await api.delete(`/communities/${cid}/${path}/${id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(errMsg(err)); } };
  const admin = can("admin");
  return (
    <Card title={title} icon={icon} testId={`${path}-card`} className="mb-5"
      right={admin && <button data-testid={`${path}-add-button`} onClick={() => setEdit({ values: toForm(fields, null) })} className={BTN_SECONDARY}><Plus className="h-4 w-4" />Add</button>}>
      {rows.length === 0 ? <EmptyState title="Nothing yet" testId={`${path}-empty`}>{emptyText}</EmptyState> : (
        <div className="overflow-x-auto">
          <Table data-testid={`${path}-table`}>
            <TableHeader><TableRow className="border-[#1E2D4A]">{columns.map(([, l]) => <TableHead key={l} className="rg-label whitespace-nowrap">{l}</TableHead>)}<TableHead /></TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`${path}-row-${r.id}`} className="border-[#1E2D4A]/60 text-sm">
                  {columns.map(([k, l, fmt]) => <TableCell key={l} className="max-w-xs truncate text-slate-300">{fmt ? fmt(r[k], r) : String(r[k] ?? "—")}</TableCell>)}
                  <TableCell className="whitespace-nowrap">{admin && <div className="flex gap-2">
                    {rowActions && rowActions(r)}
                    <button data-testid={`${path}-edit-${r.id}`} aria-label="Edit" onClick={() => setEdit({ id: r.id, values: toForm(fields, r) })} className="text-slate-300 hover:text-white"><Pencil className="h-4 w-4" /></button>
                    <ConfirmButton testId={`${path}-delete-${r.id}`} className="text-slate-300 hover:text-red-400" title="Delete?" description="This cannot be undone." onConfirm={() => remove(r.id)}><Trash2 className="h-4 w-4" /></ConfirmButton>
                  </div>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-[#1E2D4A] bg-[#0B1120] sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-display uppercase tracking-wider">{edit?.id ? "Edit" : "Add"} {title}</DialogTitle></DialogHeader>
          {edit && <form onSubmit={save} className="space-y-5"><FormFields fields={fields} values={edit.values} setValues={(v) => setEdit({ ...edit, values: v })} /><button data-testid={`${path}-save-button`} className={BTN_PRIMARY}><Save className="h-4 w-4" />Save</button></form>}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export const itemsFmt = (items) => (items || []).map((i) => `${i.amount}× ${i.shortname}`).join(", ");
